/**
 * Teacher Assignments Service
 * Handles all teacher assignment operations and relationships with saas_teachers
 * One module for one supabase service - following the app's architecture rule
 */

import { supabaseAdmin as supabase } from '../lib/supabase.js';

class TeacherAssignmentsService {
  /**
   * Get all teachers for a specific tenant
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<Array>} Array of teachers
   */
  async getAllTeachers(tenantId) {
    try {
      if (!tenantId) {
        throw new Error('tenantId is required for getAllTeachers');
      }

      const { data, error } = await supabase
        .from('saas_teachers')
        .select('id, name, code, english_name, department, teacher_no, status')
        .eq('tenant_id', tenantId)
        .eq('status', 'active')
        .order('teacher_no', { ascending: true });

      if (error) {
        throw new Error(`Failed to fetch teachers: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching teachers:', error);
      throw error;
    }
  }

  /**
   * Get class teachers from saved assignments
   * @param {string} tenantId - Tenant ID
   * @param {string} className - Class name (e.g., "Class 1A")
   * @returns {Promise<Array>} Array of class teachers with their assignments
   */
  async getClassTeachers(tenantId, className) {
    try {
      if (!tenantId) {
        throw new Error('tenantId is required for getClassTeachers');
      }

      // Get assignments for the specific class
      const { data: assignments, error: assignmentsError } = await supabase
        .from('teacher_assignments')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('class_id', className);

      if (assignmentsError) {
        throw new Error(`Failed to fetch class assignments: ${assignmentsError.message}`);
      }

      if (!assignments || assignments.length === 0) {
        return [];
      }

      // Get teacher information for each assignment
      const teacherIds = [...new Set(assignments.map(a => a.teacher_id))];
      const { data: teachers, error: teachersError } = await supabase
        .from('saas_teachers')
        .select('id, name, code, english_name, department, teacher_no')
        .eq('tenant_id', tenantId)
        .in('id', teacherIds);

      if (teachersError) {
        throw new Error(`Failed to fetch teachers: ${teachersError.message}`);
      }

      // Combine assignments with teacher information
      const classTeachers = assignments.map(assignment => {
        const teacher = teachers.find(t => t.id === assignment.teacher_id);
        const result = {
          ...assignment,
          teacher_name: teacher ? teacher.name : 'Unknown Teacher',
          teacher_english_name: teacher ? teacher.english_name : '',
          teacher_department: teacher ? teacher.department : '',
          teacher_code: teacher ? teacher.code : '',
          teacher_no: teacher ? teacher.teacher_no : null
        };
        
        // Debug logging to see what fields are available
        console.log('Assignment data for teacher mapping:', {
          assignment_id: assignment.id,
          teacher_id: assignment.teacher_id,
          assignment_type: assignment.assignment_type,
          subject_name: assignment.subject_name,
          class_id: assignment.class_id,
          teacher_name: result.teacher_name
        });
        
        return result;
      });

      return classTeachers;
    } catch (error) {
      console.error('Error fetching class teachers:', error);
      throw error;
    }
  }

  /**
   * Get all teachers with their assignments for a specific tenant
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<Array>} Array of teachers with their assignments
   */
  async getTeachersWithAssignments(tenantId) {
    try {
      // First, get all teachers for the tenant
      const { data: teachers, error: teachersError } = await supabase
        .from('saas_teachers')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('status', 'active')
        .order('teacher_no', { ascending: true });

      if (teachersError) {
        throw new Error(`Failed to fetch teachers: ${teachersError.message}`);
      }

      // Then, get all teacher assignments for the tenant
      const { data: assignments, error: assignmentsError } = await supabase
        .from('teacher_assignments')
        .select('*')
        .eq('tenant_id', tenantId);

      if (assignmentsError) {
        throw new Error(`Failed to fetch teacher assignments: ${assignmentsError.message}`);
      }

      // Combine teachers with their assignments
      const teachersWithAssignments = teachers.map(teacher => {
        const teacherAssignments = assignments.filter(assignment => assignment.teacher_id === teacher.id);
        return {
          ...teacher,
          assignments: teacherAssignments,
          assignmentCount: teacherAssignments.length
        };
      });

      return teachersWithAssignments;
    } catch (error) {
      console.error('Error fetching teachers with assignments:', error);
      throw error;
    }
  }

  /**
   * Get a specific teacher with their assignments
   * @param {string} tenantId - Tenant ID
   * @param {string} teacherId - Teacher ID (UUID)
   * @returns {Promise<Object>} Teacher with their assignments
   */
  async getTeacherWithAssignments(tenantId, teacherId) {
    try {
      // Get teacher details
      const { data: teacher, error: teacherError } = await supabase
        .from('saas_teachers')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('id', teacherId)
        .single();

      if (teacherError) {
        throw new Error(`Failed to fetch teacher: ${teacherError.message}`);
      }

      // Get teacher assignments
      const { data: assignments, error: assignmentsError } = await supabase
        .from('teacher_assignments')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('teacher_id', teacherId)
        .order('assignment_type');

      if (assignmentsError) {
        throw new Error(`Failed to fetch teacher assignments: ${assignmentsError.message}`);
      }

      return {
        ...teacher,
        assignments: assignments || [],
        assignmentCount: assignments ? assignments.length : 0
      };
    } catch (error) {
      console.error('Error fetching teacher with assignments:', error);
      throw error;
    }
  }

  /**
   * Add a new assignment to a teacher
   * @param {Object} assignmentData - Assignment data
   * @returns {Promise<Object>} Created assignment
   */
  async addTeacherAssignment(assignmentData) {
    try {
      const { data, error } = await supabase
        .from('teacher_assignments')
        .insert([assignmentData])
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to add teacher assignment: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('Error adding teacher assignment:', error);
      throw error;
    }
  }

  /**
   * Update a teacher assignment
   * @param {string} assignmentId - Assignment ID
   * @param {Object} updateData - Update data
   * @returns {Promise<Object>} Updated assignment
   */
  async updateTeacherAssignment(assignmentId, updateData) {
    try {
      const { data, error } = await supabase
        .from('teacher_assignments')
        .update(updateData)
        .eq('id', assignmentId)
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to update teacher assignment: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('Error updating teacher assignment:', error);
      throw error;
    }
  }

  /**
   * Delete a teacher assignment
   * @param {string} assignmentId - Assignment ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteTeacherAssignment(assignmentId) {
    try {
      const { error } = await supabase
        .from('teacher_assignments')
        .delete()
        .eq('id', assignmentId);

      if (error) {
        throw new Error(`Failed to delete teacher assignment: ${error.message}`);
      }

      return true;
    } catch (error) {
      console.error('Error deleting teacher assignment:', error);
      throw error;
    }
  }

  /**
   * Get teachers by assignment type
   * @param {string} tenantId - Tenant ID
   * @param {string} assignmentType - Assignment type (subject_teacher, class_master, etc.)
   * @returns {Promise<Array>} Teachers with specific assignment type
   */
  async getTeachersByAssignmentType(tenantId, assignmentType) {
    try {
      const { data, error } = await supabase
        .from('teacher_assignments')
        .select(`
          *,
          saas_teachers (
            id,
            name,
            english_name,
            subject_specialty,
            department,
            position,
            email,
            phone,
            code
          )
        `)
        .eq('tenant_id', tenantId)
        .eq('assignment_type', assignmentType);

      if (error) {
        throw new Error(`Failed to fetch teachers by assignment type: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('Error fetching teachers by assignment type:', error);
      throw error;
    }
  }

  /**
   * Get assignment statistics for a tenant
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<Object>} Assignment statistics
   */
  async getAssignmentStatistics(tenantId) {
    try {
      const { data, error } = await supabase
        .from('teacher_assignments')
        .select('assignment_type, subject_name, class_id')
        .eq('tenant_id', tenantId);

      if (error) {
        throw new Error(`Failed to fetch assignment statistics: ${error.message}`);
      }

      const stats = data.reduce((acc, assignment) => {
        acc[assignment.assignment_type] = (acc[assignment.assignment_type] || 0) + 1;
        return acc;
      }, {});

      const subjectStats = data
        .filter(a => a.assignment_type === 'subject_teacher' && a.subject_name)
        .reduce((acc, assignment) => {
          acc[assignment.subject_name] = (acc[assignment.subject_name] || 0) + 1;
          return acc;
        }, {});

      const classStats = data
        .filter(a => a.class_id)
        .reduce((acc, assignment) => {
          acc[assignment.class_id] = (acc[assignment.class_id] || 0) + 1;
          return acc;
        }, {});

      return {
        totalAssignments: data.length,
        byType: stats,
        subjectTeachers: stats.subject_teacher || 0,
        classMasters: stats.class_master || 0,
        committeeMembers: stats.committee_member || 0,
        departmentHeads: stats.department_head || 0,
        subjectDistribution: subjectStats,
        classDistribution: classStats
      };
    } catch (error) {
      console.error('Error fetching assignment statistics:', error);
      throw error;
    }
  }

  /**
   * Get teacher by code (abbreviation)
   * @param {string} tenantId - Tenant ID
   * @param {string} code - Teacher code/abbreviation
   * @returns {Promise<Object|null>} Teacher object or null
   */
  async getTeacherByCode(tenantId, code) {
    try {
      const { data, error } = await supabase
        .from('saas_teachers')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('code', code)
        .eq('status', 'active')
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null; // No teacher found with this code
        }
        throw new Error(`Failed to fetch teacher by code: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('Error fetching teacher by code:', error);
      throw error;
    }
  }

  /**
   * Bulk insert teacher assignments
   * @param {Array} assignments - Array of assignment objects
   * @returns {Promise<Array>} Created assignments
   */
  async bulkInsertAssignments(assignments) {
    try {
      const { data, error } = await supabase
        .from('teacher_assignments')
        .insert(assignments)
        .select();

      if (error) {
        throw new Error(`Failed to bulk insert assignments: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('Error bulk inserting assignments:', error);
      throw error;
    }
  }

  /**
   * Get all teacher codes for a tenant
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<Object>} Mapping of code to teacher info
   */
  async getTeacherCodeMapping(tenantId) {
    try {
      // Validate tenantId
      if (!tenantId) {
        throw new Error('tenantId is required for getTeacherCodeMapping');
      }

      console.log('Getting teacher code mapping for tenantId:', tenantId);

      const { data, error } = await supabase
        .from('saas_teachers')
        .select('id, name, code, english_name, department, teacher_no')
        .eq('tenant_id', tenantId)
        .eq('status', 'active')
        .order('teacher_no', { ascending: true });

      if (error) {
        throw new Error(`Failed to fetch teacher code mapping: ${error.message}`);
      }

      console.log('Raw teacher data from database:', data);
      console.log('Number of teachers found:', data ? data.length : 0);

      const mapping = {};
      if (data && data.length > 0) {
        data.forEach(teacher => {
          if (teacher.code) {
            console.log(`Mapping teacher code: ${teacher.code} -> ${teacher.name}`);
            mapping[teacher.code] = {
              id: teacher.id,
              name: teacher.name,
              english_name: teacher.english_name,
              department: teacher.department,
              teacher_no: teacher.teacher_no
            };
          }
        });
      }

      // Add manual mappings for teacher codes that don't match between class files and database
      const manualMappings = {
        '徐': mapping['文'], // 徐文超 - class file has "徐" but database has "文"
        '廸': null, // Teacher not found in database
        '民': null, // Teacher not found in database
      };

      // Add manual mappings to the main mapping object
      Object.keys(manualMappings).forEach(code => {
        if (manualMappings[code]) {
          mapping[code] = manualMappings[code];
          console.log(`Added manual mapping: ${code} -> ${manualMappings[code].name}`);
        } else {
          console.log(`No mapping found for teacher code: ${code}`);
        }
      });

      console.log('Created teacher code mapping:', mapping);
      console.log('Number of mapped codes:', Object.keys(mapping).length);
      return mapping;
    } catch (error) {
      console.error('Error fetching teacher code mapping:', error);
      throw error;
    }
  }

  /**
   * Validate assignment data
   * @param {Object} assignmentData - Assignment data to validate
   * @returns {Object} Validation result
   */
  validateAssignmentData(assignmentData) {
    const errors = [];

    if (!assignmentData.tenant_id) {
      errors.push('Tenant ID is required');
    }

    if (!assignmentData.teacher_id) {
      errors.push('Teacher ID is required');
    }

    if (!assignmentData.assignment_type) {
      errors.push('Assignment type is required');
    }

    const validAssignmentTypes = ['subject_teacher', 'class_master', 'committee_member', 'department_head'];
    if (assignmentData.assignment_type && !validAssignmentTypes.includes(assignmentData.assignment_type)) {
      errors.push('Invalid assignment type');
    }

    if (assignmentData.assignment_type === 'subject_teacher' && !assignmentData.subject_name) {
      errors.push('Subject name is required for subject teachers');
    }

    if (assignmentData.assignment_type === 'committee_member' && !assignmentData.committee_name) {
      errors.push('Committee name is required for committee members');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

export default new TeacherAssignmentsService();
